import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, TrendingUp, Layers, Target, Zap,
  ChevronRight, Sparkles, Building2, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { PageShell } from "@/components/SidebarLayout";
import HorizonSelector from "@/components/HorizonSelector";
import CountUp from "@/components/CountUp";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { useProjectFilter } from "@/lib/ProjectFilterContext";
import { useTheme } from "@/lib/ThemeContext";

/* -------------------------------------------------------------------------- */
/*  ANALYTICS PAGE — "Cashflow Intelligence"                                  */
/*  Head-turner analyses derived entirely client-side from the /dashboard     */
/*  endpoint. Sections:                                                        */
/*    1. Cashflow Health Score (composite gauge)                              */
/*    2. Concentration Risk (Pareto)                                          */
/*    3. Milestone Density Heatmap                                            */
/*    4. Peak / Trough Runway                                                 */
/* -------------------------------------------------------------------------- */

export default function Analytics() {
  const { project: projectFilter } = useProjectFilter();
  const [horizon, setHorizon] = useState(18);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { horizon, ...(projectFilter !== "all" && { project: projectFilter }) };
      const { data } = await api.get("/dashboard", { params });
      setDashboard(data);
    } catch (e) {
      toast.error("Failed to load analytics", { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [horizon, projectFilter]);

  const insights = useMemo(() => computeInsights(dashboard), [dashboard]);

  return (
    <PageShell
      testId="analytics-page"
      kicker="Cashflow Intelligence"
      title="Analytics"
      rangeMonths={dashboard?.timeline?.map((t) => t.month)}
      rangeLabel={`${horizon}m`}
      actions={<HorizonSelector value={horizon} onChange={setHorizon} months={dashboard?.timeline?.map((t) => t.month)} />}
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-2xl">
        Deep signal across your projected receipts — health, concentration,
        milestone density and peak/trough coverage. All computed live from your
        current sales bookings + milestone plan.
      </p>

      {loading && !dashboard && (
        <div className="cf-card p-10 text-center text-muted-light text-sm">Loading intelligence…</div>
      )}

      {insights && (
        <>
          <HealthScoreCard insights={insights} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-5">
            <ConcentrationRiskCard insights={insights} />
            <PeakTroughCard insights={insights} />
          </div>
          <MilestoneHeatmap insights={insights} />
          <TypeMixEvolutionCard insights={insights} />
        </>
      )}
    </PageShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  COMPUTE INSIGHTS                                                          */
/* -------------------------------------------------------------------------- */
function computeInsights(dashboard) {
  if (!dashboard?.projects?.length || !dashboard?.timeline?.length) return null;

  const projects = [...dashboard.projects].sort((a, b) => b.projected_total - a.projected_total);
  const timeline = dashboard.timeline;
  const total = dashboard.kpi_projected_total || projects.reduce((s, p) => s + p.projected_total, 0);

  // Pareto — top N with cumulative %
  const paretoAll = projects.map((p, i) => {
    const share = total > 0 ? p.projected_total / total : 0;
    return { ...p, share };
  });
  let cum = 0;
  const pareto = paretoAll.map((p) => {
    cum += p.share;
    return { ...p, cumulative: cum };
  });
  const top1Share = pareto[0]?.share ?? 0;
  const top3Share = pareto.slice(0, 3).reduce((s, p) => s + p.share, 0);
  const top5Share = pareto.slice(0, 5).reduce((s, p) => s + p.share, 0);

  // Peak / trough — filter zeros for trough so we get the smallest *positive* inflow, not an empty month
  const monthly = timeline.map((t) => t.projected);
  const peakIdx = monthly.reduce((a, v, i, arr) => (v > arr[a] ? i : a), 0);
  const positiveIdx = monthly.reduce((acc, v, i) => (v > 0 ? [...acc, i] : acc), []);
  const troughIdx = positiveIdx.length
    ? positiveIdx.reduce((a, i) => (monthly[i] < monthly[a] ? i : a), positiveIdx[0])
    : monthly.reduce((a, v, i, arr) => (v < arr[a] ? i : a), 0);
  const peak = timeline[peakIdx];
  const trough = timeline[troughIdx];
  const meanM = monthly.reduce((s, x) => s + x, 0) / (monthly.length || 1);
  const stddev = Math.sqrt(monthly.reduce((s, x) => s + Math.pow(x - meanM, 2), 0) / (monthly.length || 1));
  const cv = meanM > 0 ? stddev / meanM : 0;
  const ratio = trough?.projected > 0 ? peak.projected / trough.projected : 0;

  // Milestone density — count per month across ALL projects
  const milestoneMonthlyCount = timeline.map((t) => {
    let count = 0;
    projects.forEach((p) => {
      if ((p.milestone_months || []).includes(t.month)) count++;
    });
    return { month: t.month, label: t.label, count, amount: t.projected, isMilestone: !!t.milestone_marker };
  });
  const milestoneEventsInHorizon = milestoneMonthlyCount.reduce((s, m) => s + m.count, 0);

  // Type mix
  const typeMix = { new: 0, ongoing: 0, land: 0 };
  projects.forEach((p) => { typeMix[p.project_type] = (typeMix[p.project_type] || 0) + p.projected_total; });

  // Type-mix evolution — monthly split by type
  const typeMixEvolution = timeline.map((t, i) => {
    const bucket = { month: t.month, label: t.label, new: 0, ongoing: 0, land: 0 };
    projects.forEach((p) => {
      bucket[p.project_type] += (p.monthly?.[i] || 0);
    });
    return bucket;
  });

  // Health score composite
  //   Concentration penalty  — up to 30 points (if top1 = 100%, deduct 30)
  //   Volatility penalty     — up to 25 points (CV of monthly)
  //   Bench diversity bonus  — up to 5 points if we have >=6 projects with milestones
  const concentrationPenalty = Math.min(30, top1Share * 60);
  const volatilityPenalty = Math.min(25, cv * 30);
  const withMilestones = projects.filter((p) => (p.milestone_months || []).length > 0).length;
  const diversityBonus = Math.min(5, withMilestones * 0.5);
  const score = Math.max(0, Math.min(100, Math.round(100 - concentrationPenalty - volatilityPenalty + diversityBonus)));

  // Trend delta — H2 vs H1
  const mid = Math.floor(monthly.length / 2);
  const h1 = monthly.slice(0, mid).reduce((s, x) => s + x, 0);
  const h2 = monthly.slice(mid).reduce((s, x) => s + x, 0);
  const trend = h1 > 0 ? (h2 - h1) / h1 : 0;

  return {
    total,
    horizonMonths: dashboard.horizon_months,
    projects,
    timeline,
    pareto,
    top1Share, top3Share, top5Share,
    peak, trough, peakIdx, troughIdx, cv, ratio,
    milestoneMonthlyCount, milestoneEventsInHorizon,
    typeMix, typeMixEvolution,
    score,
    healthDrivers: {
      concentration: { value: top1Share, penalty: concentrationPenalty },
      volatility:    { value: cv,        penalty: volatilityPenalty },
      diversity:     { value: withMilestones, bonus: diversityBonus },
    },
    trend,
  };
}

/* -------------------------------------------------------------------------- */
/*  1. HEALTH SCORE                                                           */
/* -------------------------------------------------------------------------- */
function HealthScoreCard({ insights }) {
  const { score, total, horizonMonths, healthDrivers, trend } = insights;
  const grade = score >= 80 ? "Excellent" : score >= 65 ? "Strong" : score >= 45 ? "Watch" : "At Risk";
  const gradeColor = score >= 80 ? "#10B981" : score >= 65 ? "#818CF8" : score >= 45 ? "#F59E0B" : "#F43F5E";

  // Ring geometry
  const size = 200;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (score / 100);

  return (
    <div className="cf-card cf-fade-up p-6 md:p-8 relative overflow-hidden">
      <div
        className="absolute pointer-events-none"
        style={{
          top: -80, right: -60, width: 320, height: 320, borderRadius: "50%",
          background: `radial-gradient(circle, ${gradeColor}22 0%, transparent 65%)`,
          filter: "blur(50px)",
        }}
      />
      <div className="relative grid grid-cols-1 lg:grid-cols-[220px_1fr_1fr] gap-8 items-center">
        {/* Ring gauge */}
        <div className="flex flex-col items-center">
          <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              <defs>
                <linearGradient id="hg-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor={gradeColor} stopOpacity="0.9" />
                  <stop offset="100%" stopColor={gradeColor} stopOpacity="0.55" />
                </linearGradient>
              </defs>
              <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--cf-hairline-strong)" strokeWidth={stroke} />
              <circle
                cx={size/2} cy={size/2} r={r} fill="none"
                stroke="url(#hg-grad)" strokeWidth={stroke}
                strokeDasharray={`${dash} ${c}`}
                strokeLinecap="round"
                transform={`rotate(-90 ${size/2} ${size/2})`}
                style={{ transition: "stroke-dasharray 1s cubic-bezier(0.16,1,0.3,1)" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-light font-bold">Health</div>
              <div className="num text-ink" style={{ fontWeight: 800, fontSize: 56, letterSpacing: "-0.04em", lineHeight: 1 }}>
                <CountUp value={score} format={(v) => Math.round(v).toString()} duration={1400} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.20em] font-bold mt-0.5" style={{ color: gradeColor }}>
                {grade}
              </div>
            </div>
          </div>
        </div>

        {/* Headline */}
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-light font-semibold mb-2">
            {horizonMonths}-month intelligence report
          </div>
          <h2 className="font-display text-ink mb-4" style={{ fontWeight: 800, fontSize: "clamp(24px, 3vw, 32px)", letterSpacing: "-0.028em", lineHeight: 1.05 }}>
            Your book projects{" "}
            <span className="gradient-text">
              <CountUp value={total} format={(v) => formatMoney(v)} duration={1200} />
            </span>{" "}
            in receipts across the horizon.
          </h2>
          <p className="text-sm text-muted-dark leading-relaxed max-w-lg">
            The health score blends <b>concentration risk</b>, <b>monthly volatility</b> and <b>milestone diversity</b>.
            {trend >= 0 ? " H2 is trending up" : " H2 is trending down"} versus H1 ({(trend*100).toFixed(1)}%).
          </p>
        </div>

        {/* Drivers */}
        <div className="space-y-3">
          <DriverBar
            icon={Target}
            label="Concentration risk"
            hint={`Top project = ${(healthDrivers.concentration.value*100).toFixed(0)}% of book`}
            penalty={healthDrivers.concentration.penalty}
            max={30}
            tone="alert"
          />
          <DriverBar
            icon={Activity}
            label="Volatility"
            hint={`Monthly CV = ${(healthDrivers.volatility.value).toFixed(2)}`}
            penalty={healthDrivers.volatility.penalty}
            max={25}
            tone="warn"
          />
          <DriverBar
            icon={Layers}
            label="Milestone diversity"
            hint={`${healthDrivers.diversity.value} projects with milestones`}
            penalty={-healthDrivers.diversity.bonus}
            max={5}
            tone="positive"
            isBonus
          />
        </div>
      </div>
    </div>
  );
}

function DriverBar({ icon: Icon, label, hint, penalty, max, tone, isBonus }) {
  const pct = Math.min(1, Math.abs(penalty) / max);
  const toneMap = {
    alert:    { bg: "rgba(244, 63, 94, 0.14)", fg: "#F43F5E" },
    warn:     { bg: "rgba(245, 158, 11, 0.14)", fg: "#F59E0B" },
    positive: { bg: "rgba(16, 185, 129, 0.14)", fg: "#10B981" },
  };
  const t = toneMap[tone] || toneMap.warn;
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: t.bg, color: t.fg }}>
          <Icon size={12} />
        </div>
        <div className="text-[13px] font-semibold text-ink flex-1">{label}</div>
        <div className="text-[11px] num font-bold" style={{ color: t.fg }}>
          {isBonus ? "+" : "−"}{Math.abs(penalty).toFixed(1)}
        </div>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--cf-hairline)" }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct * 100}%`,
            background: t.fg,
            transition: "width 1.2s cubic-bezier(0.16,1,0.3,1)",
          }}
        />
      </div>
      <div className="mt-1 text-[11px] text-muted-light">{hint}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  2. CONCENTRATION RISK (PARETO)                                            */
/* -------------------------------------------------------------------------- */
function ConcentrationRiskCard({ insights }) {
  const { pareto, top3Share, top5Share, total } = insights;
  const top = pareto.slice(0, 6);
  const others = pareto.slice(6);
  const othersSum = others.reduce((s, p) => s + p.projected_total, 0);
  const bars = othersSum > 0
    ? [...top, { project_name: `${others.length} others`, project_type: "other", projected_total: othersSum, share: othersSum/total }]
    : top;

  const maxShare = bars[0]?.share || 1;
  const riskLevel = top3Share >= 0.7 ? { label: "High", color: "#F43F5E" }
                   : top3Share >= 0.5 ? { label: "Moderate", color: "#F59E0B" }
                   : { label: "Well spread", color: "#10B981" };

  return (
    <div className="cf-card cf-fade-up p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-semibold">Concentration risk</div>
          <div className="mt-1 font-display text-lg text-ink" style={{ fontWeight: 700 }}>Where is the money coming from?</div>
        </div>
        <div className="cf-pill" style={{ background: `${riskLevel.color}20`, color: riskLevel.color, border: `1px solid ${riskLevel.color}40` }}>
          <AlertTriangle size={10} /> {riskLevel.label}
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3">
        <div className="rounded-xl p-3" style={{ background: "var(--cf-surface-2)" }}>
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-bold">Top 3 share</div>
          <div className="num text-ink mt-1" style={{ fontWeight: 800, fontSize: 22 }}>
            <CountUp value={top3Share*100} format={(v) => `${v.toFixed(1)}%`} duration={1000} />
          </div>
        </div>
        <div className="rounded-xl p-3" style={{ background: "var(--cf-surface-2)" }}>
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-bold">Top 5 share</div>
          <div className="num text-ink mt-1" style={{ fontWeight: 800, fontSize: 22 }}>
            <CountUp value={top5Share*100} format={(v) => `${v.toFixed(1)}%`} duration={1000} />
          </div>
        </div>
      </div>

      <div className="space-y-2.5">
        {bars.map((p, i) => (
          <ParetoBar key={p.project_name} rank={i+1} project={p} maxShare={maxShare} />
        ))}
      </div>
    </div>
  );
}

function ParetoBar({ rank, project, maxShare }) {
  const width = (project.share / maxShare) * 100;
  const typeColor = {
    new: "#F43F5E", ongoing: "#818CF8", land: "#34D399", other: "#71717A",
  }[project.project_type] || "#71717A";
  return (
    <div className="group">
      <div className="flex items-center justify-between text-xs mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-muted-light font-mono text-[10px] w-5 text-right">{rank}</span>
          <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: typeColor }} />
          <span className="text-ink font-semibold truncate">{project.project_name}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="num text-muted-dark">{formatMoney(project.projected_total)}</span>
          <span className="num text-ink font-bold" style={{ minWidth: 44, textAlign: "right" }}>{(project.share*100).toFixed(1)}%</span>
        </div>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--cf-hairline)" }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${width}%`,
            background: `linear-gradient(90deg, ${typeColor} 0%, ${typeColor}bb 100%)`,
            transition: "width 0.9s cubic-bezier(0.16,1,0.3,1)",
          }}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  3. PEAK / TROUGH RUNWAY                                                   */
/* -------------------------------------------------------------------------- */
function PeakTroughCard({ insights }) {
  const { peak, trough, timeline, ratio, cv } = insights;
  const monthly = timeline.map((t) => t.projected);
  const max = Math.max(...monthly, 1);
  const barHeights = monthly.map((v) => (v / max) * 100);

  return (
    <div className="cf-card cf-fade-up p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-semibold">Peak / Trough analysis</div>
          <div className="mt-1 font-display text-lg text-ink" style={{ fontWeight: 700 }}>Where are the big swings?</div>
        </div>
        <div className="cf-pill cf-pill-ink">
          <Zap size={10} /> {ratio.toFixed(1)}× spread
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-xl p-3" style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.24)" }}>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold" style={{ color: "#10B981" }}>
            <ArrowUpRight size={11} /> Peak · {peak?.label}
          </div>
          <div className="num text-ink mt-1" style={{ fontWeight: 800, fontSize: 22 }}>{formatMoney(peak?.projected || 0)}</div>
        </div>
        <div className="rounded-xl p-3" style={{ background: "rgba(244,63,94,0.10)", border: "1px solid rgba(244,63,94,0.24)" }}>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold" style={{ color: "#F43F5E" }}>
            <ArrowDownRight size={11} /> Trough · {trough?.label}
          </div>
          <div className="num text-ink mt-1" style={{ fontWeight: 800, fontSize: 22 }}>{formatMoney(trough?.projected || 0)}</div>
        </div>
      </div>

      <div className="flex items-end gap-1 h-28">
        {barHeights.map((h, i) => {
          const isPeak = i === insights.peakIdx;
          const isTrough = i === insights.troughIdx;
          const color = isPeak ? "#10B981" : isTrough ? "#F43F5E" : "var(--cf-muted-2)";
          const opacity = isPeak || isTrough ? 1 : 0.35;
          return (
            <div key={i} className="flex-1 h-full flex flex-col justify-end items-center gap-1 group">
              <div
                className="w-full rounded-t"
                style={{
                  height: `${Math.max(6, h * 0.82)}%`,
                  background: color,
                  opacity,
                  transition: "height 0.9s cubic-bezier(0.16,1,0.3,1)",
                }}
                title={`${timeline[i].label}: ${formatMoney(timeline[i].projected)}`}
              />
              <div className="text-[9px] text-muted-light num" style={{ height: 14, lineHeight: "14px" }}>
                {timeline[i].label.slice(0, 3)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-[11px] text-muted-light leading-relaxed">
        Volatility coefficient (CV) = <span className="text-ink font-semibold num">{cv.toFixed(2)}</span> —
        {cv < 0.3 ? " smooth cashflow" : cv < 0.6 ? " moderate swings" : " lumpy inflows, plan treasury cover"}.
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  4. MILESTONE DENSITY HEATMAP                                              */
/* -------------------------------------------------------------------------- */
function MilestoneHeatmap({ insights }) {
  const { projects, timeline, milestoneEventsInHorizon } = insights;

  // Build project × month matrix of collections (monthly[i])
  const rows = projects.slice(0, 15); // cap for readability

  // Find max cell to normalise heat
  let cellMax = 0;
  rows.forEach((p) => {
    (p.monthly || []).forEach((v) => { if (v > cellMax) cellMax = v; });
  });
  cellMax = cellMax || 1;

  return (
    <div className="cf-card cf-fade-up p-6">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-semibold">Milestone density</div>
          <div className="mt-1 font-display text-lg text-ink" style={{ fontWeight: 700 }}>When does each project deliver cash?</div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-dark">
          <span>Low</span>
          <div className="w-32 h-2 rounded-full" style={{ background: "linear-gradient(90deg, rgba(244,63,94,0.08) 0%, rgba(244,63,94,0.28) 45%, rgba(244,63,94,0.75) 100%)" }} />
          <span>High</span>
          <span className="ml-3 cf-pill cf-pill-accent"><Sparkles size={10} /> {milestoneEventsInHorizon} events</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: 720 }}>
          <thead>
            <tr>
              <th
                className="sticky left-0 z-10 text-left text-[10px] uppercase tracking-widest font-bold px-3 py-2"
                style={{ background: "var(--cf-surface-2)", color: "var(--cf-muted-1)", minWidth: 160 }}
              >
                Project
              </th>
              {timeline.map((t) => (
                <th
                  key={t.month}
                  className="text-[9.5px] uppercase tracking-widest font-bold px-1 py-2 text-center"
                  style={{ background: "var(--cf-surface-2)", color: "var(--cf-muted-1)", minWidth: 44 }}
                >
                  {t.label.slice(0, 3)}<br />
                  <span className="text-[8.5px] opacity-60">{t.label.slice(-2)}</span>
                </th>
              ))}
              <th className="text-[10px] uppercase tracking-widest font-bold px-2 py-2 text-right" style={{ background: "var(--cf-surface-2)", color: "var(--cf-muted-1)" }}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, ri) => (
              <tr key={p.project_id} style={{ borderTop: "1px solid var(--cf-hairline)" }}>
                <td
                  className="sticky left-0 z-10 px-3 py-1.5"
                  style={{
                    background: ri % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)",
                    borderRight: "1px solid var(--cf-hairline)",
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: p.project_type === "new" ? "#F43F5E" : p.project_type === "ongoing" ? "#818CF8" : "#34D399" }}
                    />
                    <span className="text-[13px] font-semibold text-ink truncate">{p.project_name}</span>
                  </div>
                </td>
                {(p.monthly || []).map((v, ci) => {
                  const intensity = v / cellMax;
                  const isMilestone = (p.milestone_months || []).includes(timeline[ci]?.month);
                  return (
                    <td
                      key={ci}
                      className="text-center align-middle relative"
                      title={`${p.project_name} · ${timeline[ci].label}: ${formatMoney(v)}${isMilestone ? " (milestone)" : ""}`}
                      style={{
                        background: v > 0
                          ? `rgba(244, 63, 94, ${Math.max(0.05, intensity * 0.72)})`
                          : "transparent",
                        height: 32,
                        transition: "background 0.6s ease",
                      }}
                    >
                      {isMilestone && (
                        <span
                          className="absolute inset-0 flex items-center justify-center pointer-events-none"
                          style={{ color: "#fff", fontSize: 9, fontWeight: 700 }}
                        >
                          ●
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className="text-right px-2 py-1.5 num text-[12px] font-bold text-ink" style={{ background: ri % 2 === 0 ? "var(--cf-surface)" : "var(--cf-surface-2)", borderLeft: "1px solid var(--cf-hairline)" }}>
                  {formatMoney(p.projected_total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length < projects.length && (
        <div className="mt-3 text-[11px] text-muted-light text-center">
          Showing top {rows.length} projects · {projects.length - rows.length} more not displayed
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  5. TYPE-MIX EVOLUTION (stacked bars)                                      */
/* -------------------------------------------------------------------------- */
function TypeMixEvolutionCard({ insights }) {
  const { typeMixEvolution, typeMix, total } = insights;
  const maxTotal = Math.max(...typeMixEvolution.map((m) => m.new + m.ongoing + m.land), 1);

  const shares = {
    new:     total ? typeMix.new / total : 0,
    ongoing: total ? typeMix.ongoing / total : 0,
    land:    total ? typeMix.land / total : 0,
  };

  return (
    <div className="cf-card cf-fade-up p-6">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-semibold">Type mix evolution</div>
          <div className="mt-1 font-display text-lg text-ink" style={{ fontWeight: 700 }}>How does new / ongoing / land split each month?</div>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <MixKey color="#F43F5E" label={`New · ${(shares.new*100).toFixed(0)}%`} />
          <MixKey color="#818CF8" label={`Ongoing · ${(shares.ongoing*100).toFixed(0)}%`} />
          <MixKey color="#34D399" label={`Land · ${(shares.land*100).toFixed(0)}%`} />
        </div>
      </div>

      <div className="flex items-end gap-1.5 h-44">
        {typeMixEvolution.map((m, i) => {
          const sum = m.new + m.ongoing + m.land;
          const hTotal = (sum / maxTotal) * 100;
          const seg = (v) => sum ? (v / sum) * hTotal : 0;
          return (
            <div key={i} className="flex-1 h-full flex flex-col justify-end items-center gap-1 group">
              <div className="w-full flex flex-col justify-end" style={{ height: "calc(100% - 16px)" }}>
                <div title={`Land: ${formatMoney(m.land)}`} style={{ height: `${seg(m.land)}%`, background: "#34D399", borderTopLeftRadius: 3, borderTopRightRadius: 3, transition: "height 0.9s cubic-bezier(0.16,1,0.3,1)" }} />
                <div title={`Ongoing: ${formatMoney(m.ongoing)}`} style={{ height: `${seg(m.ongoing)}%`, background: "#818CF8", transition: "height 0.9s cubic-bezier(0.16,1,0.3,1)" }} />
                <div title={`New: ${formatMoney(m.new)}`} style={{ height: `${seg(m.new)}%`, background: "#F43F5E", transition: "height 0.9s cubic-bezier(0.16,1,0.3,1)" }} />
              </div>
              <div className="text-[9px] text-muted-light num" style={{ height: 14, lineHeight: "14px" }}>{m.label.slice(0,3)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MixKey({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
      <span className="text-muted-dark">{label}</span>
    </div>
  );
}
